import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const stats = [
  { value: '$12.4M+', label: 'Recovered for Homeowners', icon: 'ri-money-dollar-circle-line' },
  { value: '1,200+', label: 'Claims Successfully Won', icon: 'ri-trophy-line' },
  { value: '98%', label: 'Client Satisfaction Rate', icon: 'ri-star-line' },
  { value: '24hr', label: 'Average Response Time', icon: 'ri-time-line' },
];

const testimonials = [
  {
    name: 'Maria G.',
    location: 'Miami, FL',
    claimType: 'Hurricane Claim',
    text: "After Hurricane Ian, my insurance company offered me $8,200 for the damage. Strive Claims fought for me and got me $67,400! I couldn't believe it. They handled everything and kept me informed every step of the way.",
    stars: 5,
    amount: '$67,400',
  },
  {
    name: 'James T.',
    location: 'Jacksonville, FL',
    claimType: 'Roof Damage Claim',
    text: 'My roof damage claim was initially denied. Strive Claims reviewed my policy, documented everything properly, and got my claim reopened and approved for $34,000. Absolutely phenomenal service!',
    stars: 5,
    amount: '$34,000',
  },
  {
    name: 'Sandra R.',
    location: 'Orlando, FL',
    claimType: 'Water Damage Claim',
    text: "I was offered $4,500 for water damage that destroyed my kitchen and two rooms. These guys got me $28,900. The process was smooth and stress-free. Highly recommend Strive Claims to any homeowner.",
    stars: 5,
    amount: '$28,900',
  },
  {
    name: 'David L.',
    location: 'Tampa, FL',
    claimType: 'Storm Damage Claim',
    text: "Professional, knowledgeable, and truly on my side. They increased my settlement by 400%. If you've had any property damage, call Strive Claims before you talk to your insurance company!",
    stars: 5,
    amount: '$41,200',
  },
  {
    name: 'Patricia M.',
    location: 'Fort Lauderdale, FL',
    claimType: 'Underpaid Claim',
    text: "My claim was underpaid for months. Strive Claims took over and tripled my settlement. They are truly experts and I'm so grateful I found them. Don't fight the insurance company alone!",
    stars: 5,
    amount: '$52,800',
  },
];

export default function TrustSection() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="trust" className="bg-[#F8F7F4] py-24 px-6" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-[#EBF4FD] border border-[#93C5F5] rounded-full px-4 py-1.5 mb-4">
            <span className="text-[#1560B8] text-sm font-semibold">TRUSTED BY THOUSANDS</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#0F1927] mb-4">
            Real Results for Real Homeowners
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            We've helped over 1,200 homeowners recover millions in insurance payouts they were rightfully owed.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className={`bg-white rounded-2xl p-4 md:p-8 text-center transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-9 h-9 md:w-12 md:h-12 flex items-center justify-center mx-auto mb-3">
                <i className={`${stat.icon} text-[#1B78D4] text-xl md:text-2xl`} />
              </div>
              <div className="text-2xl md:text-4xl font-extrabold text-[#1B78D4] mb-1 md:mb-2 leading-tight">{stat.value}</div>
              <div className="text-gray-500 text-xs md:text-sm font-medium leading-tight">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap justify-center gap-6 mb-16">
          {[
            { icon: 'ri-shield-star-line', title: 'Licensed Public Adjusters', sub: 'State Certified & Bonded' },
            { icon: 'ri-hand-coin-line', title: 'No Recovery = No Fee', sub: 'You Pay Nothing Upfront' },
            { icon: 'ri-medal-line', title: 'A+ BBB Rating', sub: '5-Star Reviewed Firm' },
            { icon: 'ri-verified-badge-line', title: 'Fully Insured', sub: 'E&O Coverage Included' },
          ].map((badge) => (
            <div
              key={badge.title}
              className="flex items-center gap-3 bg-white border-2 border-[#93C5F5] rounded-xl px-6 py-4"
            >
              <div className="w-10 h-10 flex items-center justify-center">
                <i className={`${badge.icon} text-[#1B78D4] text-2xl`} />
              </div>
              <div>
                <div className="text-[#0F1927] font-bold text-sm">{badge.title}</div>
                <div className="text-gray-400 text-xs">{badge.sub}</div>
              </div>
            </div>
          ))}
        </div>

        <h3 className="text-3xl font-extrabold text-[#0F1927] text-center mb-10">What Our Clients Say</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={t.name}
              className={`bg-white rounded-2xl p-7 transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              } ${i === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`}
              style={{ transitionDelay: `${200 + i * 80}ms` }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-0.5">
                  {Array.from({ length: t.stars }).map((_, j) => (
                    <i key={j} className="ri-star-fill text-[#1B78D4] text-base" />
                  ))}
                </div>
                <span className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">
                  Settled: {t.amount}
                </span>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-5 italic">"{t.text}"</p>
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-[#1B78D4] rounded-full text-white font-bold text-sm">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <div className="text-[#0F1927] font-bold text-sm">{t.name}</div>
                  <div className="text-gray-400 text-xs">{t.location} &bull; {t.claimType}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
